import { ChevronDown, ChevronRight } from "lucide-react";
import { UserAvatar } from "@/components/ui/UserAvatar";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn, formatDisplayName } from "@/lib/utils";
import type { ParticipantRow } from "@/components/dashboard/ParticipantsOverviewTable";
import type {
  ModuleAggregate,
  ModuleParticipantBucket,
} from "@/components/dashboard/group-overview/aggregateModuleStats";

/** Matches accordion header: chevron `size-4` + `gap-1.5`. */
const CONTENT_INDENT = "pl-[1.375rem]";

interface ModuleAccordionRowProps {
  module: ModuleAggregate;
  isOpen: boolean;
  onToggle: () => void;
  onSelectParticipant: (row: ParticipantRow) => void;
}

const CountChip = ({
  count,
  borderClass,
  textClass,
  tooltip,
}: {
  count: number;
  borderClass: string;
  textClass: string;
  tooltip: string;
}) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <span
        className={cn(
          "inline-flex items-center justify-center size-5 rounded-full border text-[10px] font-bold tabular-nums bg-background cursor-default",
          borderClass,
          textClass
        )}
      >
        {count}
      </span>
    </TooltipTrigger>
    <TooltipContent side="top">{tooltip}</TooltipContent>
  </Tooltip>
);

const SectionLabel = ({
  count,
  label,
  tone = "muted",
}: {
  count: number;
  label: string;
  tone?: "muted" | "success" | "secondary";
}) => {
  const toneClasses = {
    success: {
      border: "border-[var(--success)]",
      text: "text-[var(--success-foreground)]",
      labelText: "text-[var(--success-foreground)]",
    },
    secondary: {
      border: "border-primary",
      text: "text-primary",
      labelText: "text-foreground",
    },
    muted: {
      border: "border-muted-foreground/40",
      text: "text-muted-foreground",
      labelText: "text-muted-foreground",
    },
  }[tone];

  return (
    <span className={cn("relative flex items-center min-h-5", CONTENT_INDENT)}>
      <span
        className={cn(
          // Center on the chevron column (first 1rem); label sits where the module title starts
          "absolute left-2 top-1/2 -translate-x-1/2 -translate-y-1/2",
          "inline-flex items-center justify-center size-5 rounded-full border text-[10px] font-bold tabular-nums bg-background",
          toneClasses.border,
          toneClasses.text
        )}
      >
        {count}
      </span>
      <span
        className={cn(
          "text-xs font-bold tracking-wide uppercase",
          toneClasses.labelText
        )}
      >
        {label}
      </span>
    </span>
  );
};

const InProgressRow = ({
  bucket,
  onSelect,
}: {
  bucket: ModuleParticipantBucket;
  onSelect: () => void;
}) => {
  const pct = Math.round(bucket.progressPercent);
  return (
    <button
      type="button"
      onClick={onSelect}
      className="flex cursor-pointer items-center gap-2.5 py-1.5 rounded-md hover:bg-accent/60 transition-colors text-left w-full"
    >
      <UserAvatar
        firstName={bucket.participant.firstName}
        lastNameInitial={bucket.participant.lastName[0] ?? ""}
        colorIndex={bucket.participant.colorIndex}
        size="md"
      />
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-foreground truncate">
          {formatDisplayName(
            bucket.participant.firstName,
            bucket.participant.lastName[0]
          )}
        </div>
        <div className="text-xs text-foreground truncate">
          {bucket.currentSubchapterTitle
            ? `${bucket.currentSubchapterTitle} · ${pct}%`
            : `in Bearbeitung · ${pct}%`}
        </div>
      </div>
    </button>
  );
};

const AvatarGroup = ({
  buckets,
  onSelect,
}: {
  buckets: ModuleParticipantBucket[];
  onSelect: (row: ParticipantRow) => void;
}) => (
  <div className="flex flex-wrap gap-y-1.5">
    {buckets.map((b, i) => {
      const name = formatDisplayName(
        b.participant.firstName,
        b.participant.lastName[0]
      );
      return (
        <Tooltip key={b.participant.userId}>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={() => onSelect(b.participant)}
              style={{ zIndex: buckets.length - i }}
              className={cn(
                "relative cursor-pointer rounded-full ring-2 ring-background transition-transform duration-200 ease-out hover:scale-125 hover:!z-50 hover:drop-shadow-md focus:outline-none focus-visible:ring-[var(--ring)]",
                i > 0 && "-ml-2.5"
              )}
              aria-label={name}
            >
              <UserAvatar
                firstName={b.participant.firstName}
                lastNameInitial={b.participant.lastName[0] ?? ""}
                colorIndex={b.participant.colorIndex}
                size="md"
              />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">{name}</TooltipContent>
        </Tooltip>
      );
    })}
  </div>
);

export const ModuleAccordionRow = ({
  module,
  isOpen,
  onToggle,
  onSelectParticipant,
}: ModuleAccordionRowProps) => {
  const Chevron = isOpen ? ChevronDown : ChevronRight;
  const completedCount = module.completed.length;
  const inProgressCount = module.inProgress.length;
  const notStartedCount = module.notStarted.length;

  return (
    <div
      className={cn(
        "rounded-lg border border-transparent",
        isOpen && "bg-accent border-border"
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex cursor-pointer items-center gap-1.5 px-2 py-2.5 text-left rounded-lg hover:bg-accent/60 transition-colors"
        aria-expanded={isOpen}
      >
        <Chevron className="size-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-semibold text-foreground truncate flex-1">
          {module.title}
        </span>
        <span className="flex items-center gap-1 shrink-0">
          <CountChip
            count={completedCount}
            borderClass="border-[var(--success)]"
            textClass="text-[var(--success-foreground)]"
            tooltip={`${completedCount} abgeschlossen`}
          />
          <CountChip
            count={inProgressCount}
            borderClass="border-primary"
            textClass="text-primary"
            tooltip={`${inProgressCount} in Bearbeitung`}
          />
          <CountChip
            count={notStartedCount}
            borderClass="border-muted-foreground/40"
            textClass="text-muted-foreground"
            tooltip={`${notStartedCount} noch nicht begonnen`}
          />
        </span>
      </button>

      {isOpen && (
        <div className="flex flex-col px-2 pb-3 pt-1">
          {inProgressCount > 0 && (
            <div className="flex flex-col gap-1.5 py-3 first:pt-1">
              <SectionLabel
                count={inProgressCount}
                label="In Bearbeitung"
                tone="secondary"
              />
              <div className={cn("flex flex-col gap-0.5", CONTENT_INDENT)}>
                {module.inProgress.map((b) => (
                  <InProgressRow
                    key={b.participant.userId}
                    bucket={b}
                    onSelect={() => onSelectParticipant(b.participant)}
                  />
                ))}
              </div>
            </div>
          )}

          {completedCount > 0 && (
            <div className="flex flex-col gap-1.5 py-3 first:pt-1">
              <SectionLabel
                count={completedCount}
                label="Abgeschlossen"
                tone="success"
              />
              <div className={CONTENT_INDENT}>
                <AvatarGroup
                  buckets={module.completed}
                  onSelect={onSelectParticipant}
                />
              </div>
            </div>
          )}

          {notStartedCount > 0 && (
            <div className="flex flex-col gap-1.5 py-3 first:pt-1">
              <SectionLabel
                count={notStartedCount}
                label="Noch nicht begonnen"
                tone="muted"
              />
              <div className={CONTENT_INDENT}>
                <AvatarGroup
                  buckets={module.notStarted}
                  onSelect={onSelectParticipant}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
